import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import '@fortawesome/fontawesome-free/css/all.min.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import './userStyle.css';
import config from '../../config.json';

const apiUrl = config.apiUrl;

const UserList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [usersData, setUsersData] = useState([]);
  const [dropdownStates, setDropdownStates] = useState({});
  const [searchInput, setSearchInput] = useState('');
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const roles = JSON.parse(localStorage.getItem('Roles'));

  const userAccess = {
    hasView: roles?.includes('View') || false,
    hasAdmin: roles?.includes('Admin') || false,
    hasApprover: roles?.includes('Approver') || false,
    hasStandard: roles?.includes('Standard') || false,
  };

    const { hasView, hasAdmin, hasStandard, hasApprover } = userAccess;


  const toggleDropdown = (itemId) => {
    setDropdownStates((prevState) => ({
      ...prevState,
      [itemId]: !prevState[itemId],
    }));
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token === null) {
      // Token is null, reload the page
      window.location.reload();
    } else {
      // Token is present, fetch employee data
      const fetchUserData = async () => {
        try {
          const response = await fetch(`${apiUrl}/User/Listing?search=${searchInput}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          });

          const data = await response.json();
          if (!response.ok) {
            if (response.status === 401) {
              // Redirect to login page if unauthorized
              window.location.href = `${config.identityUrl}/Identity/Account/Login`;
            } else {
              // Handle other error cases
              setErrorMessage('Failed to fetch data');
              setSuccessMessage('');
              return;
            }

          }

          setUsersData(data.Users);
        } catch (error) {
          setErrorMessage('Error fetching data');
          setSuccessMessage('');
        }
        setIsLoading(false);
      };

      fetchUserData();
    }
  }, [searchInput]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = usersData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(usersData.length / itemsPerPage);

  const pageRange = 5;
  const startPage = Math.max(1, currentPage - Math.floor(pageRange / 2));
  const endPage = Math.min(totalPages, startPage + pageRange - 1);

  return (
    <div>
      {userAccess.hasAdmin ? (
        <>
          <h2>Users</h2>
          {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
          {successMessage && <div className="alert alert-success">{successMessage}</div>}
          <div className="custom-width mb-3">
            <input
              type="text"
              placeholder="Search"
              className="form-control"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          <table className="custom-table">
            <thead>
              <tr>
                <th></th>
                <th>Last Name</th>
                <th>First Name</th>
                <th>Middle Name</th>
                <th>Email</th>
                <th>Company Code</th>
                <th>Role</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="9">
                    <div class="d-flex align-items-center">
                      <div class="spinner-border me-2" aria-hidden="true"></div>
                      <span role="status"> Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : (
              currentItems && currentItems.length > 0 ? (
                currentItems.map((item) => (
                  <tr key={item.ID}>
                    <td className="text-sm-center remove-space">
                      <div className="dropdown">
                        <button
                          className="btn"
                          type="button"
                          id={`dropdownMenuButton${item.ID}`}
                          data-bs-toggle="dropdown"
                          data-bs-auto-close="outside"
                          aria-haspopup="true"
                          aria-expanded={dropdownStates[item.ID]}
                          onClick={() => toggleDropdown(item.ID)}
                        >
                          <i className="fas fa-ellipsis"></i>
                        </button>
                        <div
                          className={`dropdown-menu ${dropdownStates[item.ID] ? 'show' : ''}`}
                          aria-labelledby={`dropdownMenuButton${item.ID}`}
                        >
                          {userAccess.hasAdmin && (
                            <Link to={`/user-updaterole/${item.ID}`} className="dropdown-item">
                              Update Role
                            </Link>
                          )}

                        </div>
                      </div>
                    </td>
                    <td>{item.LastName}</td>
                    <td>{item.FirstName}</td>
                    <td>{item.MiddleName}</td>
                    <td>{item.Email}</td>
                    <td>{item.UserCompany.CompanyCode}</td>
                    <td>{item.Role.NormalizedName}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7">No Record</td>
                </tr>
              ))}
            </tbody>
          </table>

          <nav aria-label="Page navigation">
            <ul className="pagination mt-3">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <Link
                  to={`/user-list?page=${currentPage - 1}`}
                  className="page-link"
                  onClick={() => handlePageChange(currentPage - 1)}
                  aria-label="Previous"
                >
                  <span aria-hidden="true">&laquo;</span>
                </Link>
              </li>
              {Array.from({ length: endPage - startPage + 1 }, (_, index) => (
                <li
                  key={index}
                  className={`page-item ${currentPage === startPage + index ? 'active' : ''}`}
                >
                  <Link
                    to={`/user-list?page=${startPage + index}`}
                    className="page-link"
                    onClick={() => handlePageChange(startPage + index)}
                  >
                    {startPage + index}
                  </Link>
                </li>
              ))}
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <Link
                  to={`/user-list?page=${currentPage + 1}`}
                  className="page-link"
                  onClick={() => handlePageChange(currentPage + 1)}
                  aria-label="Next"
                >
                  <span aria-hidden="true">&raquo;</span>
                </Link>
              </li>
            </ul>
          </nav>
        </>
      ) : (
        <div>
          <h3>No access!</h3>
          <button className="btn btn-primary" onClick={() => navigate(-1)}>
            Go Back
          </button>
        </div>
      )}
    </div>
  );
};

export default UserList;